import { useTranslation } from 'react-i18next';
import type { IUtilizationDay } from '@apis/equipment';
import { EQUIPMENT_STATUS_COLOR, type IEquipmentAsset } from '../equipment.types';
import styles from './AssetDetail.module.css';
import { EquipmentIcon } from '../ui/EquipmentIcon/EquipmentIcon';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { StatusDot } from '@components/ui/StatusDot/StatusDot';
import { useEquipmentActivity } from '../hooks/useEquipmentActivity';
import { useEquipmentTimeline } from '../hooks/useEquipmentTimeline';
import { WeeklyUtilization } from './WeeklyUtilization/WeeklyUtilization';
import { formatDuration } from './formatDuration';
import { MovementTimeline } from './MovementTimeline/MovementTimeline';
import { MaintenanceSchedule } from './MaintenanceSchedule/MaintenanceSchedule';
import { MaintenanceFlag } from '../ui/MaintenanceFlag/MaintenanceFlag';
import { ClockIcon, CloseIcon, MapPinIcon, TagIcon, WrenchIcon } from '@/utils/icons';

interface IAssetDetailProps {
  asset: IEquipmentAsset;
  siteId: string | null;
  onClose: () => void;
}

/**
 * The past days, then today's bar once it lands. The two come from separate
 * queries that can straddle a site midnight, so only days before today stay.
 */
function weekWithToday(past: IUtilizationDay[], today: IUtilizationDay | undefined): IUtilizationDay[] {
  if (!today) return past;
  return [...past.filter((d) => d.date < today.date), today];
}

export function AssetDetail({ asset, siteId, onClose }: IAssetDetailProps) {
  const { t } = useTranslation();
  const dash = '—';
  const zoneText = asset.floorNick ?? asset.areaNick ?? dash;
  const activity = useEquipmentActivity(siteId, asset.equipmentId);
  const timeline = useEquipmentTimeline(siteId, asset.equipmentId);
  const todayStops = timeline.data?.stops ?? [];
  const utilization = weekWithToday(activity.data?.utilization ?? [], timeline.data?.today);
  // An empty week isn't "no data" yet while today's bar may still bring some.
  const weekLoading = activity.isLoading || (timeline.isLoading && !utilization.some((d) => d.hasData || d.pending));
  // Time in use today, not time on site: the bar's minutes, never the stops' durations.
  const todayBar = timeline.data?.today;
  const runtimeText = timeline.isLoading || !todayBar?.hasData
    ? dash
    : formatDuration(todayBar.activeMinutes, t);

  return (
    <aside className={styles.panel} aria-label={t('equipment.detail.title')}>
      <ScrollArea className={styles.scroll} viewportClassName={styles.body}>
        <header className={styles.header}>
          <div className={styles.titleRow}>
            <EquipmentIcon />
            <h2 className={styles.title}>{t('equipment.detail.title')}</h2>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label={t('equipment.detail.close')}
          >
            <CloseIcon />
          </button>
        </header>

        <div className={styles.identity}>
          <h3 className={styles.assetName}>{asset.name}</h3>
          <div className={styles.identityMeta}>
            {asset.serialNumber && (
              <span className={styles.codeChip}>{asset.serialNumber}</span>
            )}
            <span className={styles.statusInline} data-status={asset.status}>
              <StatusDot color={EQUIPMENT_STATUS_COLOR[asset.status]} animate={asset.status === 'active'} />
              <span>{t(`equipment.statusLabel.${asset.status}`).toUpperCase()}</span>
            </span>
            {asset.needsMaintenance && <MaintenanceFlag />}
          </div>
        </div>

        <dl className={styles.facts}>
          <Fact
            icon={<TagIcon size={12} />}
            label={t('equipment.detail.fields.type')}
            value={asset.typeNick}
          />
          <Fact icon={<MapPinIcon size={12} />} label={t('equipment.detail.fields.zone')} value={zoneText} />
          <Fact icon={<ClockIcon size={12} />} label={t('equipment.detail.fields.runtime')} value={runtimeText} />
          <Fact
            icon={<WrenchIcon size={12} />}
            label={t('equipment.detail.fields.contractor')}
            value={asset.company ?? dash}
          />
        </dl>

        <WeeklyUtilization
          isLoading={weekLoading}
          isError={activity.isError}
          utilization={utilization}
          todayDate={todayBar?.date}
        />

        <MovementTimeline
          isLoading={timeline.isLoading}
          isError={timeline.isError}
          todayStops={todayStops}
          assetStatus={asset.status}
        />

        <MaintenanceSchedule key={asset.equipmentId} siteId={siteId} equipmentId={asset.equipmentId} />
      </ScrollArea>
    </aside>
  );
}

interface IFactProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function Fact({ icon, label, value }: IFactProps) {
  return (
    <div className={styles.fact}>
      <dt className={styles.factLabel}>
        {icon}
        <span>{label}</span>
      </dt>
      <dd className={styles.factValue}>{value}</dd>
    </div>
  );
}
